/**
 * POS-I240: the product's ONE "Powered by" line. Imported by the frontend and the backend through
 * the @shared alias. The name and url are the product's own values from the video repo's
 * src/shared/products.json (pos.name, pos.url); change them only here (two constants).
 * The line is product identity, never tenant data: it is not part of receiptLayout or the receipt
 * snapshot and no setting can switch it off.
 */
export const POWERED_BY_NAME = 'GridX POS';
export const POWERED_BY_URL = 'https://pos.gridx.app';
export const POWERED_BY_HOST = 'pos.gridx.app';
export const POWERED_BY_LABEL = 'Powered by';
/** Marker attribute: lets the e-mail sender detect an already appended footer (no double append). */
export const POWERED_BY_MARKER = 'data-powered-by';

export const POWERED_BY = {
  name: POWERED_BY_NAME,
  url: POWERED_BY_URL,
  host: POWERED_BY_HOST,
} as const;

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export interface PoweredByHtmlOptions {
  /** Translated words for "Powered by" (default English). */
  label?: string;
  /** Print/paper mode: plain text name plus the host, no link (screen keeps the link). */
  paper?: boolean;
  /** Extra inline style appended to the wrapper. */
  style?: string;
}

/** The line as HTML: "Powered by <a href=url>GridX POS</a>", small, muted, centered. */
export function poweredByHtml(opts: PoweredByHtmlOptions = {}): string {
  const label = escapeHtml(opts.label ?? POWERED_BY_LABEL);
  const name = escapeHtml(POWERED_BY_NAME);
  const base =
    'text-align:center;font-size:10px;line-height:1.4;color:#6b7280;margin:16px 0 0;font-family:Arial,Helvetica,sans-serif;';
  const style = base + (opts.style ?? '');
  const nameHtml = opts.paper
    ? `<bdi dir="ltr">${name}</bdi> <bdi dir="ltr">${escapeHtml(POWERED_BY_HOST)}</bdi>`
    : `<a href="${escapeHtml(POWERED_BY_URL)}" target="_blank" rel="noopener noreferrer" style="color:#6b7280;text-decoration:underline"><bdi dir="ltr">${name}</bdi></a>`;
  return `<div ${POWERED_BY_MARKER}="1" style="${style}">${label} ${nameHtml}</div>`;
}

/** Plain text: "Powered by GridX POS". */
export function poweredByText(label: string = POWERED_BY_LABEL): string {
  return `${label} ${POWERED_BY_NAME}`;
}

/** Plain text with host, for PDFs: "Powered by GridX POS - pos.gridx.app". */
export function poweredByTextWithHost(label: string = POWERED_BY_LABEL): string {
  return `${poweredByText(label)} - ${POWERED_BY_HOST}`;
}

function wrapWords(text: string, width: number): string[] {
  const out: string[] = [];
  let cur = '';
  for (const word of text.split(/\s+/).filter(Boolean)) {
    let w = word;
    while (w.length > width) {
      if (cur) {
        out.push(cur);
        cur = '';
      }
      out.push(w.slice(0, width));
      w = w.slice(width);
    }
    if (!cur) cur = w;
    else if ((cur + ' ' + w).length <= width) cur += ' ' + w;
    else {
      out.push(cur);
      cur = w;
    }
  }
  if (cur) out.push(cur);
  return out;
}

const centerLine = (s: string, width: number): string =>
  s.length >= width ? s : ' '.repeat(Math.floor((width - s.length) / 2)) + s;

/**
 * Thermal receipt lines (ESC/POS, fixed-width): "Powered by GridX POS" then the host, centered,
 * wrapped so that no line is longer than `width` characters (48 on 80 mm, 32 on 58 mm).
 */
export function poweredByThermalLines(width: number): string[] {
  const w = Math.max(8, Math.floor(width) || 32);
  return [...wrapWords(poweredByText(), w), ...wrapWords(POWERED_BY_HOST, w)].map((l) =>
    centerLine(l, w),
  );
}
